"use client";

import { calendarDay } from "@simplybusiness/icons";
import type {
  TextFieldElementType,
  TextFieldProps,
  Validation,
} from "@simplybusiness/mobius";
import {
  Button,
  Icon,
  TextField,
  VisuallyHidden,
  useValidationClasses,
} from "@simplybusiness/mobius";
import classNames from "classnames/dedupe";
import type { ChangeEvent, FocusEvent } from "react";
import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import { isTouchDevice } from "../../utils/isTouchDevice";
import { formatErrorMessageText, validateDateFormat } from "./utils";
import "./DatePicker.css";

const DatePickerModal = lazy(() => import("./DatePickerModal"));

const getValidationState = (isInvalid: boolean | undefined) => {
  if (isInvalid) {
    return true;
  }

  if (isInvalid === false) {
    return false;
  }

  return undefined;
};

export interface DatePickerProps
  extends
    Validation,
    Omit<TextFieldProps, "defaultValue" | "onChange" | "onBlur" | "onFocus"> {
  onChange?: (date: string | undefined) => void;
  defaultValue?: string;
  min?: string;
  max?: string;
  id?: string;
}

export const DatePicker = (props: DatePickerProps) => {
  const {
    onChange,
    defaultValue = "",
    isDisabled,
    isInvalid,
    errorMessage = "",
    ...otherProps
  } = props;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [top, setTop] = useState<number>(0);
  const inputRef = useRef<TextFieldElementType | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [textFieldVal, setTextFieldVal] = useState<string>(
    validateDateFormat(defaultValue),
  );
  const [isValid, setIsValid] = useState<boolean | undefined>(undefined);
  const isInvalidProp = getValidationState(isValid === false || isInvalid);
  const errorMessageText = isInvalidProp
    ? formatErrorMessageText(textFieldVal, props.min, props.max)
    : errorMessage;
  const touchDevice = isTouchDevice();

  const validationClasses = useValidationClasses({ isInvalid: isInvalidProp });

  const containerClasses = classNames(
    "mobius-date-picker__container",
    {
      "--is-disabled": isDisabled,
      "--is-touch-device": touchDevice,
    },
    validationClasses,
  );

  const popoverToggleClasses = classNames(
    "mobius-date-picker__field-button",
    validationClasses,
  );

  const validate = useCallback(() => {
    if (isOpen) return;

    // If 'min' or 'max' values are provided, checkValidity() will
    // validate the date and return a boolean
    const isValidInput = inputRef.current?.checkValidity();

    if (!isValidInput) {
      setIsValid(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!inputRef.current) return;
    const validatedValue = validateDateFormat(defaultValue);
    setTextFieldVal(validatedValue);
    inputRef.current.value = validatedValue;
    setIsValid(true);
    validate();
  }, [defaultValue, validate]);

  const togglePopoverVisibility = () => {
    setIsValid(true);
    setIsOpen(!isOpen);
  };

  const handleTextFieldChange = (event: ChangeEvent<TextFieldElementType>) => {
    setTextFieldVal(event.target.value);
    // onChange only triggers on a valid date
    // so this clears the error
    setIsValid(true);
  };

  const onDateSelected = (selectedDate: string | undefined) => {
    // Handle null callback from useOnClickOutside
    if (selectedDate) {
      setTextFieldVal(selectedDate);
      setIsValid(true);
      // Add other callback events here
      onChange?.(selectedDate);
    }
    // Focus the input
    inputRef.current?.focus();
    setIsOpen(false);
  };

  // User has interacted with the component and navigated away
  const handleBlur = (event: FocusEvent) => {
    const target = event.target as TextFieldElementType;
    // If the blur is caused by focusing on the DayPicker modal, do nothing
    // The modal is rendered after the input in the DOM,
    // so check if the relatedTarget is inside the modal
    if (
      isOpen &&
      event.relatedTarget &&
      containerRef.current?.contains(event.relatedTarget as HTMLElement)
    ) {
      return;
    }
    validate();

    // User hasn't entered a date, or entered an invalid date
    if (!textFieldVal) {
      setIsValid(false);
    }

    onChange?.(target.value);
  };

  const handleClick = (event: MouseEvent<TextFieldElementType>) => {
    event.preventDefault();
    togglePopoverVisibility();
  };

  useEffect(() => {
    if (isOpen) {
      setTop(containerRef.current?.getBoundingClientRect().height || 0);
      // Disable validation when day picker is open
      setIsValid(true);
      return;
    }

    validate();
  }, [isOpen, validate]);

  if (touchDevice) {
    return (
      <div className={containerClasses}>
        <TextField
          ref={inputRef}
          type="date"
          className="mobius-date-picker"
          onBlur={handleBlur}
          onChange={handleTextFieldChange}
          value={textFieldVal}
          isDisabled={isDisabled}
          isInvalid={isInvalidProp}
          {...otherProps}
          errorMessage={errorMessage || errorMessageText}
        />
      </div>
    );
  }

  return (
    <div className={containerClasses} ref={containerRef}>
      <TextField
        ref={inputRef}
        type="date"
        className="mobius-date-picker"
        onBlur={handleBlur}
        onChange={handleTextFieldChange}
        // @ts-expect-error onClick is not in TextField's props but is passed through via otherProps spread
        onClick={handleClick}
        value={textFieldVal}
        isDisabled={isDisabled}
        isInvalid={isInvalidProp}
        {...otherProps}
        errorMessage={errorMessage || errorMessageText}
        suffixOutside={
          <Button
            className={popoverToggleClasses}
            onClick={togglePopoverVisibility}
            isDisabled={isDisabled}
            size="sm"
          >
            <Icon size="sm" icon={calendarDay} />
            <VisuallyHidden>Pick date</VisuallyHidden>
          </Button>
        }
      />
      {isOpen && (
        <Suspense>
          <DatePickerModal
            date={textFieldVal}
            isOpen={isOpen}
            top={top}
            onSelected={onDateSelected}
            min={props.min}
            max={props.max}
          />
        </Suspense>
      )}
    </div>
  );
};
